import React, {useEffect, useState} from "react"
import cx from "classnames"
import {Button} from "antd"
import {useTranslation} from "react-i18next"
import {useHistory} from "react-router-dom"
import {stringify} from "query-string"
import {getFullName, handleError} from "helpers"
import {getDefaultOrFirstProfile} from "helpers/task"
import {useCurrentProfilePermissions, useDateTimeFormatters, useModel, useVisible} from "hooks"
import {Icon} from "components/Icon"
import {BaseDatePicker} from "components/DateTimePicker"
import {SignaturePopup} from "uiKit"
import {studentService} from "services"
import {Auth} from "types/auth"
import {BaseDepartmentId} from "types/departments"
import {Permissions, PermissionUserType} from "types/permission"
import {StudentChecklistItem, StudentChecklistStatus} from "types/students"
import styles from "./StudentDepartmentChecklistItem.module.css"

type Props = {
    item: StudentChecklistItem
    profileId: number
    departmentId?: number
    student?: Auth.UserProfile
    onReviewed?: () => void
}

const STATUS_LABEL_KEYS: Record<StudentChecklistStatus, string> = {
    pending: "checklist.pending",
    approved: "checklist.approved",
    rejected: "checklist.rejected"
}

function getChecklistEditPermissions(departmentId?: number): PermissionUserType {
    switch (departmentId) {
        case BaseDepartmentId.Admissions:
            return {staff: [Permissions.Staff.Admissions.Students.Checklist.Edit]}
        case BaseDepartmentId.FinancialAid:
            return {staff: [Permissions.Staff.FinancialAid.Students.Checklist.Edit]}
        case BaseDepartmentId.Academics:
            return {staff: [Permissions.Staff.Academics.Instructional.Students.Checklist.Edit]}
        case BaseDepartmentId.StudentServices:
            return {
                staff: [Permissions.Staff.StudentServicesAndAcademicAffairs.StudentServices.Students.Checklist.Edit]
            }
        default:
            return {}
    }
}

export function StudentDepartmentChecklistItem(props: Props) {
    const {item, profileId, departmentId, student, onReviewed} = props
    const {t} = useTranslation(["common"])
    const {formatDateTime} = useDateTimeFormatters()
    const history = useHistory()
    const model = useModel()
    const currentProfilePermissions = useCurrentProfilePermissions()
    const signaturePopup = useVisible(false)
    const [isRejecting, setIsRejecting] = useState(false)
    const [isApproving, setIsApproving] = useState(false)
    const [dateReceived, setDateReceived] = useState<string | null>(item.dateReceived ?? null)
    const name = item.name || item.checklistItem?.name
    const targetDepartmentId = departmentId ?? item.departmentId
    const hasEditPermission = currentProfilePermissions.hasPermissions(getChecklistEditPermissions(targetDepartmentId))
    const isStudent = model.isStudent()
    const isActionDisabled = item.isRequired === false
    const uploadedDocument = item.document
    const hasUploadedDocument = Boolean(uploadedDocument || item.documentId)
    const uploadedDocumentName = uploadedDocument?.name || name

    const renderStatusValue = (
        status: StudentChecklistStatus,
        signature: StudentChecklistItem["approvalSignature"],
        updatedAt: string | null
    ) => {
        if (status === "approved") {
            return (
                <div className={cx(styles.rowFieldValue, styles.rowStatusValue, styles.rowCompletedValue)}>
                    <div className={styles.rowStatusIcon}>
                        <Icon icon="CHECKMARK_CIRCLE" color="#18A957" className={styles.rowActivityIcon} />
                    </div>
                    <div className={styles.rowStatusValue}>
                        {updatedAt ? formatDateTime(updatedAt) : "-"}
                        <br />
                        {t("action.by")} {getFullName(signature) || t("message.system")}
                    </div>
                </div>
            )
        }

        if (status === "rejected") {
            return (
                <div className={cx(styles.rowFieldValue, styles.rowStatusValue, styles.rowRejectedValue)}>
                    <div className={styles.rowStatusIcon}>
                        <Icon icon="CROSS_CIRCLE" color="#DF1642" className={styles.rowActivityIcon} />
                    </div>
                    <div className={styles.rowStatusValue}>
                        {updatedAt ? formatDateTime(updatedAt) : "-"}
                        <br />
                        {t("action.by")} {getFullName(signature) || t("message.system")}
                    </div>
                </div>
            )
        }

        return <div className={styles.rowFieldValue}>{t(STATUS_LABEL_KEYS[status] ?? "checklist.pending")}</div>
    }

    useEffect(() => {
        setDateReceived(item.dateReceived ?? null)
    }, [item.dateReceived, item.studentChecklistItemId])

    const onChangeDateReceived = async (newDate: string | null) => {
        const previousDate = dateReceived
        setDateReceived(newDate)
        try {
            await studentService.updateStudentChecklistDateReceived({
                studentChecklistItemId: item.studentChecklistItemId,
                dateReceived: newDate || null
            })
            onReviewed?.()
        } catch (error) {
            setDateReceived(previousDate)
            handleError(error)
        }
    }

    const onClickReview = () => {
        if (isActionDisabled || !hasEditPermission) {
            return
        }
        signaturePopup.open()
    }

    const updateApprovalStatus = async (approvalStatus: Extract<StudentChecklistStatus, "approved" | "rejected">) => {
        if (isRejecting || isApproving) {
            return
        }
        try {
            if (approvalStatus === "rejected") {
                setIsRejecting(true)
            } else {
                setIsApproving(true)
            }
            await studentService.updateStudentApprovalStatus({
                studentChecklistItemId: item.studentChecklistItemId,
                approvalStatus
            })
            signaturePopup.close()
            onReviewed?.()
        } catch (error) {
            handleError(error)
        } finally {
            setIsRejecting(false)
            setIsApproving(false)
        }
    }

    const onClickOpenDocument = () => {
        if (uploadedDocument?.documentUrl) {
            window.open(uploadedDocument.documentUrl)
            return
        }
        if (!item.documentId) {
            return
        }
        history.push({
            pathname: "/documents/detail",
            search: `?${stringify({
                id: item.documentId,
                userProfileId: profileId
            })}`
        })
    }

    const onClickUpload = () => {
        if (isActionDisabled || !(hasEditPermission || isStudent) || hasUploadedDocument) {
            return
        }
        const {pathname, search} = history.location
        const backUrl = `${pathname}${search}`
        const submitDataAfterSuccess = {studentChecklistItemId: item.studentChecklistItemId}
        history.push({
            pathname: "/documents/detail",
            search: `?${stringify({
                backUrl,
                userProfileId: profileId,
                studentChecklistItemId: item.studentChecklistItemId
            })}`,
            state: {
                submitDataAfterSuccess,
                selectedChecklistItem: {
                    studentChecklistItemId: item.studentChecklistItemId,
                    name: item.checklistItem?.name,
                    profileId
                },
                documentName: item.checklistItem?.name || item.name,
                departmentId: targetDepartmentId,
                ownerUserId: student?.customUserId || student?.id,
                ownerUserCustomProfileId: student
                    ? getDefaultOrFirstProfile(student, Auth.UserProfileType.Student)?.customProfileId
                    : undefined,
                documentDepartmentSubunitId: item.checklistItem?.documentDepartmentSubunitId,
                lockDepartmentSubunit: true
            }
        })
    }

    const renderApprovalValue = () => {
        if (item.approvalStatus !== "pending") {
            return renderStatusValue(item.approvalStatus, item.approvalSignature, item.approvalStatusUpdatedAt)
        }

        return (
            <div
                className={cx(styles.actionLink, {[styles.actionLinkDisabled]: isActionDisabled})}
                onClick={onClickReview}>
                {t("checklist.review")}
            </div>
        )
    }

    const renderDocumentUploadValue = () => {
        if (hasUploadedDocument) {
            return (
                <div className={styles.fileName} onClick={onClickOpenDocument}>
                    {uploadedDocumentName}
                </div>
            )
        }

        return (
            <div
                className={cx(styles.actionLink, {[styles.actionLinkDisabled]: isActionDisabled})}
                onClick={onClickUpload}>
                {t("checklist.upload")}
            </div>
        )
    }

    return (
        <div className={styles.rowWrap}>
            <div className={styles.rowTitleWrap}>
                <div className={styles.itemName} title={name}>
                    {name}
                </div>
                <div className="flex-1" />
                <span className={cx(styles.requiredBadge, {[styles.requiredBadge__optional]: !item.isRequired})}>
                    {item.isRequired ? t("checklist.required") : t("checklist.notRequired")}
                </span>
            </div>
            <div className={styles.rowFieldWrap}>
                <div className={styles.rowFieldTitle}>{t("checklist.approval")}</div>
                {renderApprovalValue()}
            </div>
            {item.approvalStatus === "approved" ? (
                <div className={styles.rowFieldWrap}>
                    <div className={styles.rowFieldTitle}>{t("checklist.dateReceived")}</div>
                    <BaseDatePicker
                        className={styles.dateReceivedPicker}
                        dateOnly
                        allowClear
                        value={dateReceived}
                        onChange={(newDate) => onChangeDateReceived(newDate || null)}
                        disabled={!hasEditPermission || !!item.isArchived}
                    />
                </div>
            ) : null}
            {item.checklistItem?.isDocumentUploadRequired ? (
                <div className={styles.rowFieldWrap}>
                    <div className={styles.rowFieldTitle}>{t("checklist.documentUpload")}</div>
                    {renderDocumentUploadValue()}
                </div>
            ) : null}
            {signaturePopup.isVisible ? (
                <SignaturePopup
                    isShow={signaturePopup.isVisible}
                    onClose={() => signaturePopup.close()}
                    title={t("checklist.pleaseReviewItem")}>
                    <div className={styles.divider}></div>
                    <div className={styles.action}>
                        <Button
                            className={cx(styles.actionBtn, styles.rejectBtn)}
                            onClick={() => updateApprovalStatus("rejected")}
                            loading={isRejecting}>
                            <span>{t("action.reject")}</span>
                            <Icon icon="CROSS_CIRCLE" color="#df1642" className={styles.actionIcon} />
                        </Button>
                        <Button
                            className={cx(styles.actionBtn, styles.approveBtn)}
                            onClick={() => updateApprovalStatus("approved")}
                            loading={isApproving}>
                            <span>{t("action.approve")}</span>
                            <Icon icon="CHECKMARK_CIRCLE_1" color="#18a957" className={styles.actionIcon} />
                        </Button>
                    </div>
                </SignaturePopup>
            ) : null}
        </div>
    )
}
